/**
 * MBA Lab — drains the one-shot analytics signal cookie.
 *
 * Three flows finish with a server-side 302 and no client touchpoint: the magic link, invite accept,
 * and the digest settings save. MbaLab.Client/Analytics/AnalyticsSignal.cs queues an event on the
 * redirect; this reads it on the next page load and reports it.
 *
 * Two ordering rules, both deliberate:
 *
 *  1. **Delete before firing, not after.** If reporting throws, the cookie is already gone, so the
 *     event cannot be picked up and counted again on the next navigation.
 *  2. **Remember the nonce.** Deleting a cookie is not atomic across tabs, and a back-navigation can
 *     replay a page from bfcache. The nonce makes a duplicate report detectable rather than assumed
 *     impossible.
 */

import { trackConversion } from './analytics.js';

const COOKIE_NAME = 'mbalab_signal';
const SEEN_KEY = 'mbalab:signal-seen';

/** Events this cookie is allowed to carry. Anything else is ignored rather than forwarded. */
const ALLOWED = ['sign_up', 'login', 'invite_accepted', 'digest_preferences_saved'];

function readCookie() {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE_NAME + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function deleteCookie() {
  try {
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = COOKIE_NAME + '=; Path=/; Max-Age=0; SameSite=Lax' + secure;
  } catch (error) { /* nothing useful to do; the 2-minute Max-Age is the backstop */ }
}

function alreadySeen(nonce) {
  try {
    if (sessionStorage.getItem(SEEN_KEY) === nonce) return true;
    sessionStorage.setItem(SEEN_KEY, nonce);
    return false;
  } catch (error) {
    // Storage blocked. Fall back to reporting - deleting the cookie first already prevents the
    // common duplicate, and under-reporting is worse than a rare double here.
    return false;
  }
}

export function drainSignal() {
  const raw = readCookie();
  if (!raw) return null;

  // Before anything that could throw.
  deleteCookie();

  const [name, nonce, method] = raw.split('|');
  if (!name || ALLOWED.indexOf(name) === -1) return null;
  if (nonce && alreadySeen(nonce)) return null;

  const params = {};
  if (method) params.method = method;

  trackConversion(name, params);
  return name;
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', drainSignal);
  } else {
    drainSignal();
  }
}
